// Data hooks of the files list and the document panel (GED-FE-03): the paged list, the writes that
// carry `If-Match` (PATCH and DELETE: the generated mutations cannot send the header) and one
// refresh that brings every document view of a space up to date.
import {
  documentsControllerList,
  useDocumentSettingsControllerGet,
  useDocumentsControllerGet,
  useDocumentsControllerRestore,
  useDocumentsControllerVersions,
  type DocumentDetailDtoOutput,
  type UpdateDocumentDto,
} from '@batiflow/api-client';
import type { Discipline, DocumentType, PhaseType } from '@batiflow/contracts';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRuntime } from '@/app/providers/runtime-provider';
import { DOCUMENTS_PAGE_SIZE, SCAN_REFRESH_MS, type SortState } from './document-model';
import { etagOf } from './model';

export interface DocumentFilters {
  discipline?: Discipline | undefined;
  phaseType?: PhaseType | undefined;
  docType?: DocumentType | undefined;
  /** Part of the code or the title. */
  q?: string | undefined;
}

const documentsUrl = (spaceId: string): string => `/api/v1/spaces/${spaceId}/documents`;

/**
 * The documents of a folder, a page at a time (`nextCursor`), filtered and sorted by the server.
 * While a version is being checked the list reads itself again, so its status turns to
 * "available" without a reload.
 */
export function useDocumentPages(
  spaceId: string,
  folderId: string,
  filters: DocumentFilters,
  sort: SortState,
) {
  const params = {
    folderId,
    ...(filters.discipline ? { discipline: filters.discipline } : {}),
    ...(filters.phaseType ? { phaseType: filters.phaseType } : {}),
    ...(filters.docType ? { docType: filters.docType } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    sort: sort.sort,
    order: sort.order,
  };
  return useInfiniteQuery({
    queryKey: [documentsUrl(spaceId), params, 'pages'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      documentsControllerList(
        spaceId,
        { ...params, limit: DOCUMENTS_PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) },
        signal,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) =>
        page.items.some(
          (item) =>
            item.latestVersionStatus === 'scanning' || item.latestVersionStatus === 'uploading',
        ),
      )
        ? SCAN_REFRESH_MS
        : false,
  });
}

/** One document with its details (`missingMetadata`, deliverables). */
export function useDocument(spaceId: string, documentId: string | null) {
  return useDocumentsControllerGet(spaceId, documentId ?? '', {
    query: { enabled: documentId !== null },
  });
}

/** The versions of a document, newest first. */
export function useDocumentVersions(spaceId: string, documentId: string) {
  return useDocumentsControllerVersions(spaceId, documentId);
}

export const useRestoreVersion = useDocumentsControllerRestore;

/** The naming pattern and index style of the organisation (every member reads them). */
export function useDocumentSettings(orgId: string | null) {
  return useDocumentSettingsControllerGet(orgId ?? '', { query: { enabled: orgId !== null } });
}

/** Refreshes every document query of the space: lists, details and version histories. */
export function useRefreshDocuments(spaceId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  const prefix = documentsUrl(spaceId);
  return () =>
    queryClient.invalidateQueries({
      predicate: (query) => {
        const [key] = query.queryKey;
        return typeof key === 'string' && key.startsWith(prefix);
      },
    });
}

export interface UpdateDocumentVariables {
  documentId: string;
  /** `version` of the document as loaded: sent as `If-Match: W/"<version>"`. */
  version: number;
  data: UpdateDocumentDto;
}

/** Document PATCH (metadata and/or a move to another folder), with `If-Match`. */
export function useUpdateDocument(spaceId: string) {
  const { fetcher } = useRuntime();
  return useMutation({
    mutationKey: ['documents', 'update', spaceId],
    mutationFn: async ({ documentId, version, data }: UpdateDocumentVariables) => {
      const response = await fetcher<DocumentDetailDtoOutput>({
        method: 'PATCH',
        url: `${documentsUrl(spaceId)}/${documentId}`,
        data,
        etag: etagOf(version),
      });
      return response.data;
    },
  });
}

/** Document DELETE (it goes to the trash), with `If-Match`. */
export function useDeleteDocument(spaceId: string) {
  const { fetcher } = useRuntime();
  return useMutation({
    mutationKey: ['documents', 'delete', spaceId],
    mutationFn: async ({ documentId, version }: { documentId: string; version: number }) => {
      await fetcher<unknown>({
        method: 'DELETE',
        url: `${documentsUrl(spaceId)}/${documentId}`,
        etag: etagOf(version),
      });
    },
  });
}
